/**
 * CurrentLocationCprOverlay — IOverlayObject that draws a horizontal line
 * at the current centerline position on CPR views, with inward-pointing
 * arrow heads on each side.
 */

import type { IViewport } from '../../viewports';
import type { IOverlayObject } from '../types';
import { snapToPixel } from './canvas-utils';

export interface CurrentLocationCprOverlayConfig {
    readonly id: string;
    readonly color: string;
}

export class CurrentLocationCprOverlay implements IOverlayObject {
    readonly id: string;
    readonly zIndex = 110;
    readonly interactive = false;
    readonly clipByCamera = false;
    visible = false;
    worldPosition: [number, number, number] | null = null;
    canvasPosition: { x: number; y: number } | null = null;

    private normalizedY = 0;
    private color: string;

    constructor(config: CurrentLocationCprOverlayConfig) {
        this.id = config.id;
        this.color = config.color;
    }

    updatePosition(normalizedY: number): void {
        this.normalizedY = normalizedY;
        this.visible = true;
    }

    updateColor(color: string): void {
        this.color = color;
    }

    hide(): void {
        this.visible = false;
    }

    hitTest(): boolean {
        return false;
    }

    render(ctx: CanvasRenderingContext2D, viewport: IViewport): void {
        if (!this.visible) return;

        const overlayLayer = viewport.getOverlayLayer();
        const canvasWidth = overlayLayer.getCssWidth();
        const canvasHeight = overlayLayer.getCssHeight();

        if (canvasWidth <= 0 || canvasHeight <= 0) return;

        const y = snapToPixel(canvasHeight * this.normalizedY, 1);
        const arrowSize = 12;

        ctx.strokeStyle = this.color;
        ctx.fillStyle = this.color;
        ctx.lineWidth = 1;

        // Full-width horizontal line
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(canvasWidth, y);
        ctx.stroke();

        // Left arrow pointing right (inward)
        this.drawTriangle(ctx, arrowSize, y, -1, arrowSize);

        // Right arrow pointing left (inward)
        this.drawTriangle(ctx, canvasWidth - arrowSize, y, 1, arrowSize);
    }

    private drawTriangle(
        ctx: CanvasRenderingContext2D,
        x: number,
        y: number,
        direction: number,
        size: number
    ): void {
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x + direction * size, y - size);
        ctx.lineTo(x + direction * size, y + size);
        ctx.closePath();
        ctx.fill();
    }
}
